import { useEffect, useMemo, useState } from "react";
import type { Preset, PresetColumn, SourceField } from "./types";
import { defaultHeaderFor, groupedSourceFields, SOURCE_FIELDS } from "./presets";
import { STATIC_FIELD } from "./source-fields";

type Props = {
  initial: Preset;
  onSave: (next: Preset) => void;
  onCancel: () => void;
};

export function PresetEditor({ initial, onSave, onCancel }: Props) {
  const [draft, setDraft] = useState<Preset>(() => ({
    ...initial,
    columns: initial.columns.map((c) => ({ ...c })),
  }));

  useEffect(() => {
    setDraft({ ...initial, columns: initial.columns.map((c) => ({ ...c })) });
  }, [initial]);

  const usedFields = useMemo(
    () => new Set(draft.columns.map((c) => c.field)),
    [draft.columns],
  );

  const updateColumn = (idx: number, patch: Partial<PresetColumn>) => {
    setDraft((d) => {
      const next = d.columns.slice();
      const cur = next[idx];
      if (!cur) return d;
      next[idx] = { ...cur, ...patch };
      return { ...d, columns: next };
    });
  };

  // Field changes need special handling for the static sentinel: when
  // entering static, seed an empty `value`; when leaving, drop the
  // stale value so it doesn't follow the user around.
  const changeField = (idx: number, field: SourceField) => {
    setDraft((d) => {
      const next = d.columns.slice();
      const cur = next[idx];
      if (!cur) return d;
      if (field === STATIC_FIELD) {
        next[idx] = { ...cur, field, value: cur.value ?? "" };
      } else {
        const { value: _drop, ...rest } = cur;
        void _drop;
        next[idx] = { ...rest, field };
      }
      return { ...d, columns: next };
    });
  };

  const removeColumn = (idx: number) => {
    setDraft((d) => ({ ...d, columns: d.columns.filter((_, i) => i !== idx) }));
  };

  const moveColumn = (idx: number, delta: -1 | 1) => {
    setDraft((d) => {
      const next = d.columns.slice();
      const target = idx + delta;
      if (target < 0 || target >= next.length) return d;
      const a = next[idx];
      const b = next[target];
      if (!a || !b) return d;
      next[idx] = b;
      next[target] = a;
      return { ...d, columns: next };
    });
  };

  const addColumn = () => {
    const firstUnused = SOURCE_FIELDS.find((f) => !usedFields.has(f.field));
    const fallback = SOURCE_FIELDS[0]!;
    const choice = firstUnused ?? fallback;
    setDraft((d) => ({
      ...d,
      columns: [...d.columns, { field: choice.field, header: choice.defaultHeader }],
    }));
  };

  const trimmedName = draft.name.trim();
  const canSave = trimmedName.length > 0 && draft.columns.length > 0;

  const submit = () => {
    if (!canSave) return;
    onSave({
      ...draft,
      name: trimmedName,
      description: draft.description.trim(),
      columns: draft.columns.map((c) => {
        const header = c.header.trim() === "" ? defaultHeaderFor(c.field) : c.header;
        if (c.field === STATIC_FIELD) {
          return { field: c.field, header, value: c.value ?? "" };
        }
        return { field: c.field, header };
      }),
    });
  };

  return (
    <div className="bg-[#1a1a1a] border-2 border-[#fbf0df] rounded-xl p-4 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-bold">Edit preset</h2>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="bg-transparent text-[#fbf0df] border-2 border-[#fbf0df] px-3 py-1 rounded-lg font-bold transition-all duration-100 hover:bg-[#fbf0df] hover:text-[#1a1a1a] cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSave}
            className="bg-[#fbf0df] text-[#1a1a1a] border-0 px-4 py-1 rounded-lg font-bold transition-all duration-100 hover:bg-[#f3d5a3] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wider text-[#fbf0df]/60">Name</span>
          <input
            type="text"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="bg-[#242424] border border-[#fbf0df]/40 rounded-md px-2 py-1.5 text-[#fbf0df] outline-none focus:border-[#fbf0df]"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wider text-[#fbf0df]/60">Description</span>
          <input
            type="text"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            placeholder="Optional"
            className="bg-[#242424] border border-[#fbf0df]/40 rounded-md px-2 py-1.5 text-[#fbf0df] outline-none focus:border-[#fbf0df] placeholder-[#fbf0df]/30"
          />
        </label>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-wider text-[#fbf0df]/60">
            Columns ({draft.columns.length})
          </span>
          <button
            onClick={addColumn}
            className="text-sm bg-transparent text-[#fbf0df] border-2 border-[#fbf0df] px-3 py-1 rounded-md font-bold hover:bg-[#fbf0df] hover:text-[#1a1a1a] cursor-pointer"
          >
            + Add column
          </button>
        </div>

        <div className="flex flex-col gap-2">
          {draft.columns.map((col, idx) => (
            <div
              key={idx}
              className="bg-[#242424] border border-[#fbf0df]/30 rounded-md px-2 py-1.5 flex flex-col gap-1.5"
            >
              <div className="grid grid-cols-[auto_1fr_1fr_auto] sm:grid-cols-[auto_2fr_3fr_auto] items-center gap-2">
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveColumn(idx, -1)}
                    disabled={idx === 0}
                    aria-label="Move up"
                    className="text-xs px-1 leading-none text-[#fbf0df]/70 hover:text-[#fbf0df] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => moveColumn(idx, 1)}
                    disabled={idx === draft.columns.length - 1}
                    aria-label="Move down"
                    className="text-xs px-1 leading-none text-[#fbf0df]/70 hover:text-[#fbf0df] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    ▼
                  </button>
                </div>

                <select
                  value={col.field}
                  onChange={(e) => changeField(idx, e.target.value as SourceField)}
                  className="bg-[#1a1a1a] border border-[#fbf0df]/40 rounded-md px-2 py-1 text-[#fbf0df] text-sm outline-none focus:border-[#fbf0df]"
                >
                  {groupedSourceFields().map((g) => (
                    <optgroup key={g.group} label={g.group}>
                      {g.fields.map((f) => (
                        <option key={f.field} value={f.field}>
                          {f.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>

                <input
                  type="text"
                  value={col.header}
                  onChange={(e) => updateColumn(idx, { header: e.target.value })}
                  placeholder="Column header"
                  className="bg-[#1a1a1a] border border-[#fbf0df]/40 rounded-md px-2 py-1 text-[#fbf0df] text-sm outline-none focus:border-[#fbf0df] placeholder-[#fbf0df]/30"
                />

                <button
                  onClick={() => removeColumn(idx)}
                  aria-label="Remove column"
                  className="text-[#fbf0df]/60 hover:text-red-400 px-2 cursor-pointer"
                >
                  ✕
                </button>
              </div>

              {col.field === STATIC_FIELD && (
                <div className="flex items-center gap-2 pl-8">
                  <span className="text-xs uppercase tracking-wider text-[#fbf0df]/40 shrink-0">
                    Value
                  </span>
                  <input
                    type="text"
                    value={col.value ?? ""}
                    onChange={(e) => updateColumn(idx, { value: e.target.value })}
                    placeholder='e.g. "pending" — or leave empty for a blank column'
                    className="flex-1 bg-[#1a1a1a] border border-[#fbf0df]/40 rounded-md px-2 py-1 text-[#fbf0df] text-sm outline-none focus:border-[#fbf0df] placeholder-[#fbf0df]/30"
                  />
                </div>
              )}
            </div>
          ))}

          {draft.columns.length === 0 && (
            <p className="text-sm text-[#fbf0df]/60 italic">
              No columns yet — add at least one to save.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

